import React from 'react';

const UpdateUser = ({ formData, setFormData, handleSubmit, memberships, onClose }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to calculate and update end date
  const updateEndDate = (membershipId) => {
    setFormData(prev => {
      const updatedMemberships = prev.memberships.map(m => {
        if (m.id === membershipId && m.pivot.start_date) {
          const startDate = new Date(m.pivot.start_date);
          const endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + m.membership_duration);
          
          return {
            ...m,
            pivot: {
              ...m.pivot,
              end_date: endDate.toISOString()
            }
          };
        }
        return m;
      });
      
      return {
        ...prev,
        memberships: updatedMemberships
      };
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="relative max-w-2xl mx-auto p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-100">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Update Profile</h2>
        <p className="text-gray-600">Manage your account information and memberships</p>
      </div>
      <div className='absolute top-4 right-4 cursor-pointer' onClick={onClose}>Close</div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="name">
            Full Name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              required
            />
          </div>
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="email">
            Email Address
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="block w-full pl-10 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              required
            />
          </div>
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="phone">
            Phone Number
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="block w-full pl-10 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
            />
          </div>
        </div>

        {/* Current Memberships */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Current Memberships
          </label>
          <div className="mt-1 space-y-3">
            {formData.memberships?.length > 0 ? (
              formData.memberships.map((membership) => (
                <div key={membership.id} className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-gray-800">{membership.membership_type}</h4>
                      <p className="text-sm text-gray-600">
                        Duration: {membership.membership_duration} days
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateEndDate(membership.id)}
                      className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 text-sm"
                    >
                      Update End Date
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Start Date: </span>
                      <span>{formatDate(membership.pivot.start_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">End Date: </span>
                      <span>{formatDate(membership.pivot.end_date)}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">No memberships assigned</p>
            )}
          </div>
        </div>

        {/* Available Memberships Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700" htmlFor="membership_ids">
            Available Membership Types
          </label>
          <div className="mt-1">
            <select
              id="membership_ids"
              name="membership_ids"
              multiple
              value={formData.membership_ids || []}
              onChange={(e) => {
                const selectedOptions = Array.from(e.target.selectedOptions, (option) =>
                  Number(option.value)
                );
                setFormData((prev) => ({
                  ...prev,
                  membership_ids: selectedOptions,
                }));
              }}
              className="block w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all h-auto min-h-[120px]"
            >
              {memberships.map((membership) => (
                <option 
                  key={membership.id} 
                  value={membership.id}
                  className="px-3 py-2 border m-2 hover:bg-indigo-50"
                >
                  {membership.membership_type} ({membership.membership_duration} days)
                </option>
              ))}
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Hold Ctrl/Cmd to select multiple memberships
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Update Profile
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateUser;